export interface HudRefs {
  holeLabel: HTMLElement;
  strokes: HTMLElement;
  par: HTMLElement;
  total: HTMLElement;
  powerBar: HTMLElement;
  powerPct: HTMLElement | null;
  courseDate: HTMLElement;
  toast: HTMLElement;
  camBtn: HTMLButtonElement;
  centerRoot: HTMLElement | null;
  centerHole: HTMLElement | null;
  centerPar: HTMLElement | null;
  centerParDots: HTMLElement | null;
}

export function getHudRefs(): HudRefs {
  return {
    holeLabel: document.getElementById("hud-hole")!,
    strokes: document.getElementById("hud-strokes")!,
    par: document.getElementById("hud-par")!,
    total: document.getElementById("hud-total")!,
    powerBar: document.getElementById("hud-power-fill")!,
    powerPct: document.getElementById("hud-power-pct"),
    courseDate: document.getElementById("course-date")!,
    toast: document.getElementById("hud-toast")!,
    camBtn: document.getElementById("hud-cam-btn") as HTMLButtonElement,
    centerRoot: document.getElementById("hud-center"),
    centerHole: document.getElementById("hud-center-hole"),
    centerPar: document.getElementById("hud-center-par"),
    centerParDots: document.getElementById("hud-center-par-dots"),
  };
}

export function setHole(
  hud: HudRefs,
  holeIndex: number,
  par: number,
  /** "endless" hides the `/ 9` suffix so the label grows past 9. */
  style: "standard" | "endless" = "standard",
): void {
  hud.holeLabel.textContent =
    style === "endless" ? `Hole ${holeIndex + 1}` : `Hole ${holeIndex + 1} / 9`;
  hud.par.textContent = `Par ${par}`;
  hud.strokes.textContent = "0";
  if (hud.centerHole) {
    hud.centerHole.innerHTML =
      style === "endless"
        ? `HOLE ${holeIndex + 1}`
        : `HOLE ${holeIndex + 1}<span class="slash"> / </span>9`;
  }
  if (hud.centerPar) hud.centerPar.textContent = `Par ${par}`;
  if (hud.centerParDots) {
    hud.centerParDots.innerHTML = Array.from({ length: par })
      .map(() => "<span></span>")
      .join("");
  }
}

export function setStrokes(hud: HudRefs, strokes: number): void {
  hud.strokes.textContent = String(strokes);
}

export function setTotal(hud: HudRefs, total: number): void {
  hud.total.textContent = String(total);
}

export function setPower(hud: HudRefs, power: number): void {
  hud.powerBar.style.transform = `scaleX(${power.toFixed(3)})`;
  hud.powerBar.style.opacity = power > 0 ? "1" : "0";
  if (hud.powerPct) {
    hud.powerPct.textContent = `${Math.round(power * 100)}%`;
  }
}

/** Toggle the whole power-meter pill. Only shown while actively
 *  aiming so the idle HUD stays clean. */
export function setPowerVisible(visible: boolean): void {
  const el = document.getElementById("hud-power");
  if (!el) return;
  el.classList.toggle("active", visible);
}

export type ToastKind = "default" | "warn" | "info";

let toastTimer: ReturnType<typeof setTimeout> | null = null;
export function showToast(
  hud: HudRefs,
  text: string,
  ms = 1800,
  kind: ToastKind = "default",
): void {
  hud.toast.textContent = text;
  hud.toast.classList.remove("kind-warn", "kind-info");
  if (kind !== "default") hud.toast.classList.add(`kind-${kind}`);
  hud.toast.classList.add("show");
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => hud.toast.classList.remove("show"), ms);
}

export type BannerKind = "celebrate" | "par" | "warn";

/** Big centered gradient banner for hole outcomes + similar milestones.
 *  Use for HOLE IN ONE / EAGLE / BIRDIE / PAR / hazard hits — anything
 *  that deserves a beat of celebration or emphasis. For quieter info
 *  (welcome text, server rejections), keep using showToast. */
let bannerTimer: ReturnType<typeof setTimeout> | null = null;
export function showBanner(
  _hud: HudRefs,
  args: {
    title: string;
    subtitle?: string;
    crown?: string;
    kind?: BannerKind;
    ms?: number;
  },
): void {
  const root = document.getElementById("hud-banner");
  if (!root) return;
  const titleEl = document.getElementById("hud-banner-title");
  const subEl = document.getElementById("hud-banner-subtitle");
  const crownEl = document.getElementById("hud-banner-crown");
  if (titleEl) titleEl.textContent = args.title;
  if (subEl) subEl.textContent = args.subtitle ?? "";
  if (crownEl) crownEl.textContent = args.crown ?? "👑";
  root.classList.remove("kind-warn", "kind-par");
  if (args.kind && args.kind !== "celebrate") {
    root.classList.add(`kind-${args.kind}`);
  }
  // Kick the element out of display:none so the transition runs;
  // toggling .show alone is enough because the base style has
  // opacity:0 + transform scale.
  root.classList.add("show");
  if (bannerTimer) clearTimeout(bannerTimer);
  bannerTimer = setTimeout(() => {
    root.classList.remove("show");
  }, args.ms ?? 2000);
}
